import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Constants } from 'app/utility/constants';
import { Capacitor } from '@capacitor/core';
import { NavigationBar, NavigationBarColor } from '@capgo/capacitor-navigation-bar';
import { StorageService } from 'app/services/storage.service';
import { NativeService } from 'app/services/native.service';

export type ThemePreference = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

@Injectable({
    providedIn: 'root'
})
export class ThemeService
{
    private readonly className = 'mc-dark';

    private readonly themeSubject = new BehaviorSubject<Theme>('light');
    public readonly theme$ = this.themeSubject.asObservable();

    private preference: ThemePreference = 'system';
    private systemMediaQuery: MediaQueryList | null = null;
    private systemMediaListener: ((event: MediaQueryListEvent) => void) | null = null;

    constructor(
        private storage: StorageService,
        private nativeService: NativeService
    )
    {
    }

    public async init(): Promise<void>
    {
        await this.hydrateStoredPreference();
        this.applyTheme(this.resolveTheme(this.preference));
        this.ensureSystemListener();
    }


    public getTheme(): Theme
    {
        return this.themeSubject.value;
    }

    public getThemePreference(): ThemePreference
    {
        return this.preference;
    }

    public isDark(): boolean
    {
        return this.getTheme() === 'dark';
    }

    public setTheme(theme: ThemePreference): void
    {
        this.preference = theme;
        this.storeThemePreference(theme);
        this.applyTheme(this.resolveTheme(theme));
        this.ensureSystemListener();
    }

    public setDarkMode(enabled: boolean): void
    {
        this.setTheme(enabled ? 'dark' : 'light');
    }

    private async hydrateStoredPreference(): Promise<void>
    {
        const stored = await this.storage.get(Constants.IonicStorage_Theme, false);
        if (stored === 'dark' || stored === 'light' || stored === 'system')
        {
            this.preference = stored;
        }
    }

    private storeThemePreference(theme: ThemePreference): void
    {
        // Non user-specific so it applies before login.
        void this.storage.set(Constants.IonicStorage_Theme, theme, false);
    }

    private resolveTheme(preference: ThemePreference): Theme
    {
        if (preference === 'system')
        {
            if (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
            {
                return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            }
            return 'light';
        }
        return preference;
    }

    private ensureSystemListener(): void
    {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        {
            return;
        }

        if (!this.systemMediaQuery)
        {
            this.systemMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        }

        if (!this.systemMediaListener)
        {
            this.systemMediaListener = () =>
            {
                if (this.preference === 'system')
                {
                    this.applyTheme(this.resolveTheme('system'));
                }
            };

            // Safari < 14 uses addListener/removeListener.
            try
            {
                this.systemMediaQuery.addEventListener('change', this.systemMediaListener);
            }
            catch
            {
                // eslint-disable-next-line deprecation/deprecation
                this.systemMediaQuery.addListener(this.systemMediaListener);
            }
        }
    }

    private applyTheme(theme: Theme): void
    {
        this.themeSubject.next(theme);

        document.documentElement.classList.toggle(this.className, theme === 'dark');

        // Hint to the browser for built-in form controls, scrollbars, etc.
        document.documentElement.style.colorScheme = theme;

        // Best-effort native nav bar icon color.
        this.updateNativeSystemUI(theme).catch(() => { });
    }

    private async updateNativeSystemUI(theme: Theme): Promise<void>
    {
        if (!Capacitor.isNativePlatform())
        {
            return;
        }

		// Keep status bar icons in sync too, while preserving page-level intent.
		// NativeService handles Android 16+ and iOS, and forces white icons when mc-dark is active.
		try
		{
			await this.nativeService.setupStatusBar(this.nativeService.currentColor);
		}
		catch { }
    }
}
